import { useState } from "react";
import { useNavigate } from "react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ExternalLink, FileText, Globe, RefreshCw } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { DetailSection } from "@/components/freelancers/DetailSection";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { linkedInApi } from "@/services/linkedin-api";
import type { HistoryEntry } from "@/types/linkedin";

interface Props {
  freelancerId: string;
}

function formatDateTime(iso: string): string {
  return new Date(iso).toLocaleString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function linkDaOrigem(entry: HistoryEntry): string | null {
  if (entry.s3_url) return entry.s3_url;
  return entry.original_filename.startsWith("https://www.linkedin.com/") ? entry.original_filename : null;
}

export function FreelancerLinkedInHistoryCard({ freelancerId }: Props) {
  const { toast } = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [reprocessingId, setReprocessingId] = useState<string | null>(null);

  const { data: history = [] } = useQuery({
    queryKey: ["freelancer-linkedin-history", freelancerId],
    queryFn: () => linkedInApi.listFreelancerHistory(freelancerId),
  });

  const { data: atual } = useQuery({
    queryKey: ["freelancer-linkedin-atual", freelancerId],
    queryFn: () => linkedInApi.getFreelancerAtual(freelancerId),
  });

  const handleReprocess = async (jobId: string | null) => {
    if (!jobId) return;
    if (!window.confirm("Reprocessar este arquivo de LinkedIn? Um novo job de extração será criado.")) return;
    try {
      setReprocessingId(jobId);
      const job = await linkedInApi.reextract(jobId);
      toast({
        title: "Reprocessamento iniciado",
        description: "Levando você para a tela de revisão...",
      });
      queryClient.invalidateQueries({ queryKey: ["freelancer-linkedin-history", freelancerId] });
      navigate(`/linkedin/jobs/${job.id}/revisar`);
    } catch (err) {
      toast({
        variant: "destructive",
        title: "Erro ao reprocessar",
        description: (err as Error).message,
      });
    } finally {
      setReprocessingId(null);
    }
  };

  if (history.length === 0 && !atual) return null;

  return (
    <DetailSection
      title="Histórico de LinkedIn"
      icon={FileText}
      count={history.length > 0 ? history.length : undefined}
    >
      <div className="space-y-3">
        {atual && (
          <div className="bg-muted/50 rounded p-3 space-y-1 text-sm">
            <div className="font-medium">Estado atual</div>
            {atual.headline && <div className="text-muted-foreground">{atual.headline}</div>}
            <div className="flex flex-wrap gap-1 text-xs">
              {atual.open_to_work && (
                <Badge className="border-green-300 bg-green-50 text-green-700" variant="outline">
                  Open to Work
                </Badge>
              )}
              {atual.senioridade && <Badge variant="outline">{atual.senioridade}</Badge>}
              {atual.anos_experiencia_total !== null && (
                <Badge variant="outline">~{atual.anos_experiencia_total} anos</Badge>
              )}
              {atual.atualizado_em && (
                <Badge variant="outline">Atualizado em {formatDateTime(atual.atualizado_em)}</Badge>
              )}
            </div>
            {atual.carreira_observacoes?.observacoes_texto?.length ? (
              <ul className="list-disc list-inside text-xs text-muted-foreground space-y-0.5 mt-2">
                {atual.carreira_observacoes.observacoes_texto.map((b, i) => (
                  <li key={i}>{b}</li>
                ))}
              </ul>
            ) : null}
          </div>
        )}

        {history.length === 0 ? (
          <p className="text-sm text-muted-foreground">Nenhuma atualização registrada.</p>
        ) : (
          <div className="divide-y">
            {history.map((h) => (
              <div key={h.id} className="flex items-start justify-between gap-3 py-2 text-sm">
                <div className="min-w-0 space-y-0.5">
                  <div className="flex items-center gap-2">
                    {h.s3_url ? (
                      <span className="font-mono text-xs truncate" title={h.original_filename}>
                        {h.original_filename}
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-xs truncate" title={h.original_filename}>
                        <Globe className="h-3 w-3" />
                        Busca automática
                      </span>
                    )}
                    {h.applied_at ? (
                      <Badge
                        variant="outline"
                        className="text-xs border-green-300 bg-green-50 text-green-700"
                      >
                        aplicado
                      </Badge>
                    ) : (
                      <Badge variant="outline" className="text-xs">
                        não aplicado
                      </Badge>
                    )}
                  </div>
                  <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-muted-foreground">
                    <span>{formatDateTime(h.created_at)}</span>
                    {h.applied_at && <span>· aplicado {formatDateTime(h.applied_at)}</span>}
                    {h.model_used && <span>· {h.model_used}</span>}
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-7 px-2 text-xs"
                    disabled={!h.extraction_job_id || reprocessingId === h.extraction_job_id}
                    onClick={() => handleReprocess(h.extraction_job_id)}
                    title={
                      h.extraction_job_id
                        ? "Reprocessar esta extração"
                        : "Sem job de extração associado"
                    }
                  >
                    <RefreshCw
                      className={`h-3 w-3 mr-1 ${
                        reprocessingId === h.extraction_job_id ? "animate-spin" : ""
                      }`}
                    />
                    Reprocessar
                  </Button>
                  {linkDaOrigem(h) && (
                    <a
                      href={linkDaOrigem(h) ?? undefined}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs text-primary inline-flex items-center"
                    >
                      {h.s3_url ? "Ver PDF" : "Ver perfil"} <ExternalLink className="h-3 w-3 ml-1" />
                    </a>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </DetailSection>
  );
}
